import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PricingPlan, UserProfile } from '../types/user';
import { ImageStyleKey } from '../components/NewDream';

const APPROACH_IDS = {
  adler: '1738060974628',
  freud: '1738060162264',
  jung: '1738060945873',
  solms: '1738061140706',
  taylor: '1738061216118',
  castaneda: '1738061011633',
  hillman: '1738061190525',
  campbell: '1735036725878',
  laberge: '1735036641916',
  grof: '1735036710664',
  csikszentmihalyi: '1764932585485',
  ullman: '1764932617104',
} as const;

type ApproachSlug = keyof typeof APPROACH_IDS;
type ApproachId = (typeof APPROACH_IDS)[ApproachSlug];

interface PlanRestrictions {
  dreamQuotaLabelKey: string;
  maxApproachesPerDream: number;
  availableApproachIds: ApproachId[];
  allowedStyles: ImageStyleKey[];
  canCombineApproaches: boolean;
  hasDetailedStats: boolean;
  hasFullDreamJournal: boolean;
  imageQuotaPerDream: number;
  totalStylesAvailable: number;
}

const ALL_STYLE_KEYS: ImageStyleKey[] = [
  'impressionism',
  'artNouveau',
  'flemishMaster',
  'surrealCinematic',
  'artNaif',
  'anime',
  'cyberManga',
  'horrorManga',
  'noirComics',
  'graphicNovel',
  'europeanComic',
  'pastelReverie',
  'neonNocturne',
  'urbanChaos',
  'radicalContrast',
  'heroicRealism',
  'pixelArt',
  'collage',
  'constructivism',
  'screenprint50s',
];

const STYLE_PACKS: Record<PricingPlan, ImageStyleKey[]> = {
  free: ['impressionism', 'anime', 'pixelArt'],
  basic: ['impressionism', 'surrealCinematic', 'anime', 'graphicNovel', 'pixelArt', 'urbanChaos'],
  premium: ALL_STYLE_KEYS,
};

const APPROACH_PACKS: Record<PricingPlan, ApproachId[]> = {
  free: [APPROACH_IDS.jung, APPROACH_IDS.campbell],
  basic: [
    APPROACH_IDS.jung,
    APPROACH_IDS.freud,
    APPROACH_IDS.campbell,
    APPROACH_IDS.solms,
    APPROACH_IDS.taylor,
    APPROACH_IDS.grof,
  ],
  premium: [
    APPROACH_IDS.adler,
    APPROACH_IDS.freud,
    APPROACH_IDS.jung,
    APPROACH_IDS.solms,
    APPROACH_IDS.taylor,
    APPROACH_IDS.castaneda,
    APPROACH_IDS.hillman,
    APPROACH_IDS.campbell,
    APPROACH_IDS.laberge,
    APPROACH_IDS.grof,
    APPROACH_IDS.csikszentmihalyi,
    APPROACH_IDS.ullman,
  ],
};

// TODO: ce bloc tourne en double avec planLimits. À fusionner dans un fichier unique.
const planRestrictions: Record<PricingPlan, PlanRestrictions> = {
  free: {
    dreamQuotaLabelKey: 'newDream.planLimits.dreamQuotaLabels.free',
    maxApproachesPerDream: 1,
    availableApproachIds: APPROACH_PACKS.free,
    allowedStyles: STYLE_PACKS.free,
    canCombineApproaches: false,
    hasDetailedStats: false,
    hasFullDreamJournal: false,
    imageQuotaPerDream: 1,
    totalStylesAvailable: STYLE_PACKS.free.length,
  },
  basic: {
    dreamQuotaLabelKey: 'newDream.planLimits.dreamQuotaLabels.basic',
    maxApproachesPerDream: 1,
    availableApproachIds: APPROACH_PACKS.basic,
    allowedStyles: STYLE_PACKS.basic,
    canCombineApproaches: false,
    hasDetailedStats: true,
    hasFullDreamJournal: true,
    imageQuotaPerDream: 1,
    totalStylesAvailable: STYLE_PACKS.basic.length,
  },
  premium: {
    dreamQuotaLabelKey: 'newDream.planLimits.dreamQuotaLabels.premium',
    maxApproachesPerDream: 2,
    availableApproachIds: APPROACH_PACKS.premium,
    allowedStyles: STYLE_PACKS.premium,
    canCombineApproaches: true,
    hasDetailedStats: true,
    hasFullDreamJournal: true,
    imageQuotaPerDream: 1,
    totalStylesAvailable: STYLE_PACKS.premium.length,
  },
};

const getCurrentPlan = (userProfile?: UserProfile | null): PricingPlan => userProfile?.plan || 'free';

export function usePlanRestrictions() {
  const { userProfile } = useAuth();
  const currentPlan = getCurrentPlan(userProfile);

  const restrictions = useMemo(() => planRestrictions[currentPlan], [currentPlan]);

  const canUseApproach = (approachId: string): boolean =>
    restrictions.availableApproachIds.includes(approachId as ApproachId);

  const canUseStyle = (style: ImageStyleKey) => restrictions.allowedStyles.includes(style);

  return {
    canUseApproach,
    canUseStyle,
    canCombineApproaches: () => restrictions.canCombineApproaches,
    hasDetailedStats: () => restrictions.hasDetailedStats,
    hasFullDreamJournal: () => restrictions.hasFullDreamJournal,
    getMaxDreamsDesc: () => restrictions.dreamQuotaLabelKey,
    getMaxApproachesPerDream: () => restrictions.maxApproachesPerDream,
    getAvailableApproachIds: () => restrictions.availableApproachIds,
    getAllowedStyles: () => restrictions.allowedStyles,
    getImageQuotaPerDream: () => restrictions.imageQuotaPerDream,
    getTotalStylesAvailable: () => restrictions.totalStylesAvailable,
    getCurrentPlan: () => currentPlan,
  };
}
